import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/utils/cn';

/** Above this length a string leaf gets a textarea instead of a single-line input. */
const LONG_TEXT_THRESHOLD = 80;

/** Humanises an object key into a field label (`section_name` / `sectionName` → "Section Name"). */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Narrows to a non-null, non-array object so the object branch can iterate its keys safely. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Empty clone of an object's shape — every key present, values blanked — so "Add" produces an item matching the existing ones. */
function emptyLike(template: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const key of Object.keys(template)) next[key] = '';
  return next;
}

interface NodeProps {
  value: unknown;
  onChange: (next: unknown) => void;
  disabled: boolean;
  /** Automation id for this node; children append `-item-{index}` (arrays) or `-{key}` (objects). */
  testId: string;
}

/**
 * Renders one node of a structured value and emits the reconstructed node on
 * edit. Recurses on arrays and objects so an array-of-objects (the common
 * enriched shape) becomes a card per item with a labelled input per key.
 */
function ValueNode({ value, onChange, disabled, testId }: NodeProps) {
  const { t } = useTranslation('content-enrichment');

  // --- Array: cards for objects, rows for primitives ---
  if (Array.isArray(value)) {
    const items: unknown[] = value;
    const setItem = (i: number, next: unknown) => {
      onChange(items.map((item, idx) => (idx === i ? next : item)));
    };
    const removeItem = (i: number) => {
      onChange(items.filter((_, idx) => idx !== i));
    };
    // A new item clones the first item's key set so the shape stays consistent;
    // a primitive list (or an empty array — button disabled) just appends a blank string.
    const addItem = () => {
      const first = items[0];
      onChange([...items, isPlainObject(first) ? emptyLike(first) : '']);
    };

    return (
      <div className="flex flex-col gap-2" data-testid={`${testId}-list`}>
        {items.map((item, i) => (
          <div
            key={i}
            className="relative rounded-lg border p-3 pr-9"
            data-testid={`${testId}-item-${String(i)}`}
          >
            <ValueNode
              value={item}
              onChange={(next) => {
                setItem(i, next);
              }}
              disabled={disabled}
              testId={`${testId}-item-${String(i)}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              onClick={() => {
                removeItem(i);
              }}
              aria-label={t('compareGrid.structuredEditor.removeItem')}
              data-testid={`${testId}-item-${String(i)}-remove-button`}
              className={cn(
                'text-muted-foreground hover:text-destructive absolute top-1.5 right-1.5 size-6',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <Trash2 className="size-3.5" aria-hidden="true" />
            </Button>
          </div>
        ))}
        {/* Disabled on an empty array: with no item to clone, the element shape is unknowable. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || items.length === 0}
          onClick={addItem}
          data-testid={`${testId}-add-item-button`}
          className={cn(
            'w-fit gap-1',
            disabled || items.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <Plus className="size-3.5" aria-hidden="true" />
          {t('compareGrid.structuredEditor.addItem')}
        </Button>
      </div>
    );
  }

  // --- Object: one labelled control per key ---
  if (isPlainObject(value)) {
    return (
      <div className="flex flex-col gap-2">
        {Object.entries(value).map(([key, val]) => (
          <div key={key} className="flex flex-col gap-1">
            <span className="text-muted-foreground text-xs">{humanizeKey(key)}</span>
            <ValueNode
              value={val}
              onChange={(next) => {
                onChange({ ...value, [key]: next });
              }}
              disabled={disabled}
              testId={`${testId}-${key}`}
            />
          </div>
        ))}
      </div>
    );
  }

  // --- Boolean: a two-option toggle so the type survives ---
  if (typeof value === 'boolean') {
    return (
      <div className="flex gap-2">
        {[true, false].map((opt) => (
          <Button
            key={String(opt)}
            type="button"
            size="sm"
            variant={value === opt ? 'default' : 'outline'}
            disabled={disabled}
            onClick={() => {
              onChange(opt);
            }}
            data-testid={`${testId}-${String(opt)}-button`}
            className={cn(disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            {t(opt ? 'compareGrid.structuredEditor.true' : 'compareGrid.structuredEditor.false')}
          </Button>
        ))}
      </div>
    );
  }

  // --- Number: keep it numeric, blanking to null rather than NaN ---
  if (typeof value === 'number') {
    return (
      <Input
        type="number"
        value={Number.isFinite(value) ? value : ''}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value === '' ? null : Number(e.target.value));
        }}
        data-testid={`${testId}-input`}
        className="bg-white text-sm"
      />
    );
  }

  // --- String / null / undefined (leaf) ---
  const text = typeof value === 'string' ? value : '';
  if (text.length > LONG_TEXT_THRESHOLD) {
    return (
      <Textarea
        value={text}
        disabled={disabled}
        rows={3}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        data-testid={`${testId}-textarea`}
        className="bg-white text-sm"
      />
    );
  }
  return (
    <Input
      value={text}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      data-testid={`${testId}-input`}
      className="bg-white text-sm"
    />
  );
}

interface StructuredValueEditorProps {
  /** Current working value in the attribute's own shape (object, array, or primitive). */
  value: unknown;
  /** Emits the reconstructed value on every edit, so the parent always holds a ready-to-send payload. */
  onChange: (next: unknown) => void;
  disabled?: boolean;
  /** Automation hook: prefix for every control; nested paths append `-item-{index}` / `-{key}`. */
  testId?: string;
}

/**
 * Edits a structured enriched value as a form of labelled inputs rather than raw
 * JSON — one control per property, cards per array item — and reconstructs the
 * value on every change. The parent persists that value directly; there is no
 * JSON to parse and nothing malformed to guard against.
 */
export function StructuredValueEditor({
  value,
  onChange,
  disabled = false,
  testId = 'structured-value-editor',
}: StructuredValueEditorProps) {
  return <ValueNode value={value} onChange={onChange} disabled={disabled} testId={testId} />;
}
